/**
 * Order-progress notification matrix (platform master gate).
 *
 * DNA re-pushes hundreds of order-line progress events per import. Rather than
 * one notification per event, the platform groups them per customer per
 * milestone (in-app) and into one digest email per customer per cadence. This
 * tab is the master switch: per milestone, whether it shows in-app and whether
 * it emails (off / digest / instant), plus the digest cadence.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Input } from '../../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Bell, Search, X, AlertTriangle } from 'lucide-react';
import { customersAPI } from '../../../lib/api';
import {
  PlatformSettings, NotificationConfig, NotificationMilestoneConfig,
  NotificationEmailMode, CustomerCadenceOverride, NotificationCadence,
} from './types';
import { SiteEmailSettingsSection } from './SiteEmailSettingsSection';
import { CustomerEmailLimitSection } from './CustomerEmailLimitSection';

interface CustomerResult { company: number; company_name: string }

const MILESTONES = [
  { key: 'order_received', label: 'Order received', hint: 'DNA "Order was received"' },
  { key: 'in_process', label: 'In process', hint: 'Work started (WIP)' },
  { key: 'ready_for_dispatch', label: 'Ready for dispatch', hint: 'Completed, awaiting despatch' },
  { key: 'dispatched', label: 'Dispatched / delivered', hint: 'Shipped back to customer' },
  { key: 'on_hold', label: 'On hold', hint: 'Line placed on hold' },
  { key: 'promise_date_change', label: 'Promise date change', hint: 'Promise date rescheduled (actual change, not first set)' },
  { key: 'asn_submitted', label: 'Shipment submitted (ASN)', hint: 'Shipment notified to the customer' },
];

/** Milestones the grouped digest job can actually carry.
 *  The digest is built from PO change-log rows, so an ASN-sourced milestone
 *  has no digest carrier — offering it would store a setting that sends
 *  nothing. Those rows are limited to Off / Instant. */
const DIGEST_CAPABLE = new Set([
  'order_received', 'in_process', 'ready_for_dispatch',
  'dispatched', 'on_hold', 'promise_date_change',
]);

const DEFAULT_MS: NotificationMilestoneConfig = { in_app: true, email: 'digest' };

interface NotificationDefaultsTabProps {
  platformSettings: PlatformSettings;
  saving: boolean;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
  onSave: () => void;
}

export function NotificationDefaultsTab({
  platformSettings,
  saving,
  onPlatformSettingsChange,
  onSave,
}: NotificationDefaultsTabProps) {
  const cfg: NotificationConfig = platformSettings.defaultNotificationPreferences || {};
  const milestones = cfg.milestones || {};
  const cadence = cfg.digest_cadence ?? 'daily';
  const digestHour = cfg.digest_hour ?? 7;

  const getMs = (key: string): NotificationMilestoneConfig => milestones[key] || DEFAULT_MS;

  const updateConfig = (patch: Partial<NotificationConfig>) => {
    onPlatformSettingsChange({
      ...platformSettings,
      defaultNotificationPreferences: { ...cfg, ...patch },
    });
  };

  const setMilestone = (key: string, patch: Partial<NotificationMilestoneConfig>) => {
    updateConfig({ milestones: { ...milestones, [key]: { ...getMs(key), ...patch } } });
  };

  // --- Per-customer cadence overrides ---
  const overrides = cfg.customer_overrides || {};
  const overrideCount = Object.keys(overrides).length;
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<CustomerResult[]>([]);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const res = await customersAPI.getProfiles({ search: q, page_size: 8 });
        if (live) setResults((res.data.results || []) as unknown as CustomerResult[]);
      } catch { if (live) setResults([]); }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [search]);

  const setOverrides = (next: Record<string, CustomerCadenceOverride>) =>
    updateConfig({ customer_overrides: next });

  const addOverride = (c: CustomerResult) => {
    if (overrides[c.company]) return;
    setOverrides({ ...overrides, [c.company]: { name: c.company_name, cadence, hour: digestHour } });
    setSearch('');
    setResults([]);
  };

  const updateOverride = (id: string, patch: Partial<CustomerCadenceOverride>) =>
    setOverrides({ ...overrides, [id]: { ...overrides[id], ...patch } });

  const removeOverride = (id: string) => {
    const next = { ...overrides };
    delete next[id];
    setOverrides(next);
  };

  // Materialise the full effective config into state once, so an unchanged
  // Save still persists every milestone + cadence (what-you-see-is-saved).
  useEffect(() => {
    if (!cfg.milestones) {
      updateConfig({
        milestones: Object.fromEntries(MILESTONES.map((m) => [m.key, getMs(m.key)])),
        digest_cadence: cadence,
        digest_hour: digestHour,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailsPaused = platformSettings.emailsPaused;

  const setEmailsPaused = (paused: boolean) => {
    onPlatformSettingsChange({ ...platformSettings, emailsPaused: paused });
  };

  return (
    <div className="space-y-4">
      {/* Global kill switch — stop all outbound email platform-wide */}
      <Card className={emailsPaused ? 'border-destructive' : ''}>
        <CardContent className="flex items-start justify-between gap-4 py-4">
          <div className="space-y-1">
            <Label className="flex items-center gap-2 text-base font-medium">
              <AlertTriangle className={`h-4 w-4 ${emailsPaused ? 'text-destructive' : 'text-muted-foreground'}`} />
              Stop all emails
            </Label>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Emergency kill switch. When ON, the platform sends <strong>no</strong>{' '}
              notification email at all — digests and per-event notifications
              alike. Transactional and security mail (invites, password resets)
              still goes out, so nobody is locked out of the portal. Queued
              emails stay pending and go out once you switch this back OFF;
              nothing is lost. Takes effect immediately on save.
            </p>
            {emailsPaused && (
              <p className="text-sm font-medium text-destructive">
                All outbound email is currently paused.
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <Switch
              checked={emailsPaused}
              onCheckedChange={setEmailsPaused}
              aria-label="Stop all emails"
            />
            <Button size="sm" variant={emailsPaused ? 'destructive' : 'outline'} onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="defaults">
        <TabsList>
          <TabsTrigger value="defaults">Platform defaults</TabsTrigger>
          <TabsTrigger value="sites">Per-site overrides</TabsTrigger>
          <TabsTrigger value="customers">Per-customer limits</TabsTrigger>
        </TabsList>

        <TabsContent value="defaults">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Order Progress Notifications
          </CardTitle>
          <CardDescription>
            Master switch for order-progress notifications. Events are grouped per
            customer to avoid flooding — in-app rolls up by milestone; email is a
            single digest per customer on the cadence below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Matrix header */}
          <div className="grid grid-cols-[1fr_90px_140px] items-center gap-3 px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span>Milestone</span>
            <span className="text-center">In-App</span>
            <span>Email</span>
          </div>
          <div className="space-y-2">
            {MILESTONES.map((m) => {
              const ms = getMs(m.key);
              return (
                <div
                  key={m.key}
                  className="grid grid-cols-[1fr_90px_140px] items-center gap-3 p-3 border rounded-lg"
                >
                  <div>
                    <Label className="text-sm">{m.label}</Label>
                    <p className="text-xs text-muted-foreground">{m.hint}</p>
                  </div>
                  <div className="flex justify-center">
                    <Switch
                      checked={ms.in_app}
                      onCheckedChange={(checked) => setMilestone(m.key, { in_app: checked })}
                    />
                  </div>
                  <Select
                    value={ms.email}
                    onValueChange={(v) => setMilestone(m.key, { email: v as NotificationEmailMode })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">Off</SelectItem>
                      {DIGEST_CAPABLE.has(m.key) && (
                        <SelectItem value="digest">Digest</SelectItem>
                      )}
                      <SelectItem value="instant">Instant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>

          {/* Mode legend — explain in-app grouping + email modes */}
          <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
            <p><strong className="text-foreground">In-App</strong> — shows a grouped card in the bell, e.g. “Ready for dispatch: 42 order lines”, expandable to the affected orders. One card per milestone per customer (never one per line).</p>
            <p><strong className="text-foreground">Email · Off</strong> — no email for this milestone.</p>
            <p><strong className="text-foreground">Email · Digest</strong> — bundled into a single summary email per customer, sent on the cadence below (recommended for high-volume milestones — avoids flooding inboxes).</p>
            <p><strong className="text-foreground">Email · Instant</strong> — its own email as soon as the digest job runs. Use only for low-volume, urgent milestones.</p>
          </div>

          {/* Digest cadence */}
          <div className="border-t pt-4 space-y-3">
            <Label className="text-base font-medium">Email digest cadence</Label>
            <p className="text-sm text-muted-foreground">
              How often digest emails are sent. The per-recipient daily cap below
              always applies on top as a safeguard.
            </p>
            <div className="flex items-center gap-3">
              <Select
                value={cadence}
                onValueChange={(v) => updateConfig({ digest_cadence: v as NotificationConfig['digest_cadence'] })}
              >
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                </SelectContent>
              </Select>
              {cadence === 'daily' && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">at hour (UTC)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={digestHour}
                    onChange={(e) => updateConfig({ digest_hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })}
                    className="w-20"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Per-recipient daily cap */}
          <div className="border-t pt-4 space-y-3">
            <Label className="text-base font-medium">Per-recipient daily email cap</Label>
            <p className="max-w-2xl text-sm text-muted-foreground">
              The most emails one recipient can receive in any rolling 24 hours.
              Anything over the cap is held back and shown in the Email Queue
              monitor. Overridable per site (see Per-site overrides) and per
              customer company. Transactional and security mail — invites and
              password resets — is always exempt.
            </p>
            <Input
              type="number"
              min={1}
              value={platformSettings.emailRateLimitPerDay}
              onChange={(e) =>
                onPlatformSettingsChange({
                  ...platformSettings,
                  emailRateLimitPerDay: Math.max(1, Number(e.target.value) || 1),
                })
              }
              className="w-24"
            />
          </div>

          {/* Restrict notification emails to joined users only */}
          <div className="flex items-start justify-between gap-4 border-t pt-4">
            <div className="space-y-1">
              <Label className="text-base font-medium">Only email joined users</Label>
              <p className="max-w-2xl text-sm text-muted-foreground">
                When ON, order-progress notification emails (instant and digest)
                are sent only to users who have joined — i.e. accepted their
                invite or logged in at least once. Invited-but-not-yet-joined
                users are skipped. Invitation and other transactional emails are
                never affected.
              </p>
            </div>
            <Switch
              checked={platformSettings.notifyJoinedUsersOnly}
              onCheckedChange={(checked) =>
                onPlatformSettingsChange({ ...platformSettings, notifyJoinedUsersOnly: checked })
              }
              aria-label="Only email joined users"
            />
          </div>

          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Notification Settings'}
          </Button>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="sites">
          <SiteEmailSettingsSection
            platformRateLimit={platformSettings.emailRateLimitPerDay}
            globallyPaused={emailsPaused}
          />
        </TabsContent>

        <TabsContent value="customers" className="space-y-4">
      {/* Primary per-customer control: the daily email cap. */}
      <CustomerEmailLimitSection platformRateLimit={platformSettings.emailRateLimitPerDay} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Per-customer digest cadence
          </CardTitle>
          <CardDescription>
            Separate from the cap above: how often a customer&apos;s digest is
            sent, not how many emails they may receive. Selected customers run
            on their own cadence; everyone else uses the platform default.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Per-customer cadence overrides */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Customers on a custom cadence</Label>
              <span className="text-xs text-muted-foreground">
                {overrideCount === 0
                  ? 'All customers use the platform default'
                  : `${overrideCount} customer${overrideCount === 1 ? '' : 's'} on a custom cadence · all others use the platform default (${cadence}${cadence === 'daily' ? ` @ ${String(digestHour).padStart(2, '0')}:00` : ''})`}
              </span>
            </div>

            {/* Search to add a customer */}
            <div className="relative max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search customer to add an override…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
              {results.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md">
                  {results.map((c) => (
                    <button
                      key={c.company}
                      type="button"
                      onClick={() => addOverride(c)}
                      disabled={!!overrides[c.company]}
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-40"
                    >
                      {c.company_name}
                      {overrides[c.company] && ' (already added)'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Override rows */}
            {overrideCount > 0 && (
              <div className="space-y-2">
                {Object.entries(overrides).map(([id, ov]) => (
                  <div
                    key={id}
                    className="grid grid-cols-[1fr_140px_90px_36px] items-center gap-3 p-2 border rounded-lg"
                  >
                    <span className="text-sm truncate">{ov.name}</span>
                    <Select
                      value={ov.cadence}
                      onValueChange={(v) => updateOverride(id, { cadence: v as NotificationCadence })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">Off</SelectItem>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                      </SelectContent>
                    </Select>
                    {ov.cadence === 'daily' ? (
                      <Input
                        type="number"
                        min={0}
                        max={23}
                        value={ov.hour}
                        onChange={(e) => updateOverride(id, { hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })}
                        className="w-20"
                      />
                    ) : <span />}
                    <Button variant="ghost" size="icon" onClick={() => removeOverride(id)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Customer Cadences'}
          </Button>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
